import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { motion, AnimatePresence } from 'framer-motion'; // ✅ 新增动画库
import { FaCircleChevronRight } from "react-icons/fa6";

/**
 * DataTable component renders a table with dynamic headers and rows.
 *
 * @param {Object[]} headers - Array of header objects.
 * @param {string} headers[].field - The field name corresponding to the data key.
 * @param {string} headers[].label - The display label for the header.
 * @param {Object[]} data - Array of data objects to be displayed in the table.
 * @param {Function} [onHeaderClick] - Optional callback function to handle header click events.
 * @param {Function} [onRowClick] - Optional callback function to handle row click events.
 * @param {Function} [renderRow] - Optional custom function to render a row. Receives the row data and index as arguments.
 * @param {Function} [renderTableBody] - Optional custom function to render a tr. Receives the full data set as arguments.
 * @param {string} [containerClassName] - Optional custom class name for the container div.
 * @param {string} [orderBy] - Optional field to indicate what field the data ordering by (with a "-" will represent DESC).
 * @param {boolean} [loading] - Optional flag to indicate if the data is loading.
 *
 * @returns {JSX.Element} The rendered table component.
 */
const DataTable = ({
    headers,
    data,
    onHeaderClick = null,
    onRowClick = null,
    renderRow = null,
    renderTableBody = null,
    orderBy = null,
    containerClassName = 'table-container',
    loading = false,
    expandable = false, // ✅ 是否支持展开
    expandMode = 'single', // ✅ 单展开 or 多展开
    expandedHeaders = null, // the fileds that showed in the expand part
    renderExpandedRow = null, // ✅ 自定义展开内容
    expandContainerClassName = 'overflow-hidden grid grid-cols-1 md:grid-cols-2 gap-y-2 gap-x-8 p-4',
}) => {
    const [expandedRows, setExpandedRows] = useState([]);
    const handleRowClick = (row, index) => {
        if (expandable) {
            if (expandMode === 'single') {
                setExpandedRows(prev => (prev[0] === index ? [] : [index]));
            } else {
                setExpandedRows(prev =>
                    prev.includes(index)
                        ? prev.filter(i => i !== index)
                        : [...prev, index]
                );
            }
        }
        // if (onRowClick) {
        //     onRowClick(row);
        // }
    };
    const isRowExpanded = (index) => expandedRows.includes(index);

    const defaultRenderRow = (row) => (
        headers.map((header, index) => (
            <td key={index}>
                {row[header.field]}
            </td>
        ))
    );
    const defaultExpandedRow = (row) => (
        expandedHeaders.map((header, index) => (
            <div key={index}>
                <lable>{header.label} : </lable>
                <span>{row[header.field]}</span>
            </div>
        ))
    );

    const defaultRenderTableBody = (data) =>
        data?.map((row, index) => (
            <React.Fragment key={index}>
                <tr
                    className={index % 2 === 0 ? 'bg-[#fdfdfd]' : 'bg-[#f7f7f7]'}
                    style={{ cursor: onRowClick ? 'pointer' : 'default' }}
                    onClick={onRowClick ? () => onRowClick(row) : undefined}
                >
                    {expandable && <td><div onClick={() => handleRowClick(row, index)}
                        className={`w-6 h-6 flex items-center justify-center transform transition-transform duration-300 ${isRowExpanded(index) ? 'rotate-90' : 'rotate-0'}  filter drop-shadow-[0_2px_2px_rgba(0,0,0,0.3)] `}>
                        <FaCircleChevronRight className="w-full h-full text-[#205dbe]" />
                    </div> </td>}
                    {renderRow ? renderRow(row, index) : defaultRenderRow(row)}
                </tr>

                <AnimatePresence initial={false}>
                    {expandable && isRowExpanded(index) && (
                        <tr className={index % 2 === 0 ? 'bg-[#fdfdfd]' : 'bg-[#f7f7f7]'}>
                            <td colSpan={expandable ? headers.length + 1 : headers.length} className="p-0">
                                <motion.div
                                    initial={{ height: 0, opacity: 0 }}
                                    animate={{ height: 'auto', opacity: 1 }}
                                    exit={{ height: 0, opacity: 0 }}
                                    transition={{ duration: 0.1 }}
                                    className={expandContainerClassName}
                                >
                                    {renderExpandedRow
                                        ? renderExpandedRow(row, expandedHeaders)
                                        : defaultExpandedRow(row)}
                                </motion.div>
                            </td >
                        </tr>
                    )}
                </AnimatePresence>
            </React.Fragment>
        ));

    const { t } = useTranslation();

    return (
        loading ? (
            <div className="spinner-container">
                <div className="spinner"></div>
            </div>
        ) : (
            <div className={containerClassName || 'table-container'}>
                <table>
                    <thead>
                        <tr>
                            {expandable && <th />}
                            {headers?.map((header, index) => (
                                <th key={index} onClick={onHeaderClick ? () => onHeaderClick(header.field) : undefined}>
                                    {t(header.label)}{orderBy === header.field && "▲"}{orderBy === ("-" + header.field) && "▼"}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {renderTableBody ? renderTableBody(data) : defaultRenderTableBody(data)}
                    </tbody>
                </table>

            </div>
        )
    );
};

DataTable.propTypes = {
    headers: PropTypes.arrayOf(
        PropTypes.shape({
            label: PropTypes.string.isRequired,
            field: PropTypes.string.isRequired,
        })
    ).isRequired,
    data: PropTypes.arrayOf(PropTypes.object).isRequired,
    onHeaderClick: PropTypes.func,
    onRowClick: PropTypes.func,
    renderRow: PropTypes.func,
    renderTableBody: PropTypes.func,
    orderBy: PropTypes.string,
    containerClassName: PropTypes.string,
    loading: PropTypes.bool, // Add loading prop type
    expandable: PropTypes.bool, // ✅ 是否支持展开
    expandMode: PropTypes.oneOf(['single', 'multiple']), // ✅ 单行 or 多行展开
    expandedHeaders: PropTypes.arrayOf(
        PropTypes.shape({
            label: PropTypes.string.isRequired,
            field: PropTypes.string.isRequired,
        })
    ),
    renderExpandedRow: PropTypes.func, // ✅ 展开内容渲染函数
};

// DataTable.defaultProps = {
//     onHeaderClick: null,
//     onRowClick: null,
//     renderRow: null,
//     containerClassName: 'table-container',
//     loading: false, // Default loading to false
// };

export default DataTable;